/**
 * Glyph size for a ROW OF ICON BUTTONS beside or beneath an input — the chat
 * composer's action row, the note editor's toolbar.
 *
 * 18px, matching the workspace drawer's entity-row glyphs
 * (`EntityListRowItem`), which is the reference these rows are read against:
 * the composer and the drawer are on screen together, and at 14 the composer's
 * controls read as smaller than the list beside them rather than as quieter
 * (reported 2026-09).
 *
 * It was 14, chosen against the 24 these rows kept reaching for — a 24px glyph
 * fills 60% of its 40px button and a row of them crowds a light surface. 18 is
 * a ~45% fill, so it keeps that argument's intent while matching the drawer.
 * Do not take it back to 24.
 *
 * MUST be a CSS length, never a Chakra scale number. `MaterialIcon` resolves a
 * scalar `boxSize` through `useToken('sizes', [...])` and then writes the
 * result straight into `font-size`. A value that is not in the theme scale
 * (4.5 was not — the scale steps 3.5 → 4 → 5) survives the lookup unchanged and
 * emits a unitless `font-size: 4.5`, which the browser drops entirely: the glyph
 * then silently inherits its button's font size, which differed per surface.
 * A `px` string is valid CSS either way, so it cannot fail that way.
 *
 * This is the GLYPH only. Every container keeps its own box, so hit areas are
 * untouched — see `Toggle` in `ConnectorPickerButton` for the pattern when a
 * small visual needs a large target.
 *
 * It lives here rather than beside either consumer because the same decision
 * was made twice, by hand, in two feature folders, and drifted both times: the
 * composer had four sizes pretending to be one, and the note toolbar's seven
 * buttons were all 24. A shared constant is what stops a third row rediscovering
 * it.
 *
 * NOT for every icon in the app. A fullscreen surface wants a bigger glyph —
 * `LiveSessionPage`'s camera controls are deliberately 24 and should stay
 * there. This is for a dense row of controls attached to an editing surface.
 */
export const ACTION_ROW_GLYPH_SIZE = '18px';

/**
 * Glyph size for an icon inside a MENU ITEM — the account menu, the help menu.
 *
 * Same 18px as {@link ACTION_ROW_GLYPH_SIZE} and the drawer's rows, for the
 * same reason: one glyph size across the chrome a researcher reads at a glance.
 *
 * It has to be STATED. `MaterialIcon` defaults to `font-size: inherit` with a
 * `1em` box, and Chakra's MenuItem sets `sm` type, so an unsized glyph in a
 * menu silently renders at 12.8px — the smallest icon in the app, and smaller
 * than any surface deliberately chose.
 */
export const MENU_ITEM_GLYPH_SIZE = '18px';
